import type { Attachment } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/attachment";
import PaperclipIcon from "@redotech/redo-icons/paperclip.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { Flex } from "@redotech/redo-web/flex";
import { cn } from "@redotech/redo-web/tailwind-utils";
import { Option } from "effect";
import type { Value } from "platejs";
import { Plate, PlateContent } from "platejs/react";
import { memo, type ReactNode } from "react";
import type { UseMessageEditorResult } from "./use-message-editor";

export const MessageEditor = memo(function MessageEditor({
  editorState,
  header,
  toolbar,
  placeholder = "Type your message...",
  onChange,
  className,
  attachments,
  onRemoveAttachment,
  actions,
}: {
  editorState: UseMessageEditorResult;
  header?: ReactNode;
  toolbar?: ReactNode;
  placeholder?: string;
  onSend?: () => void;
  onChange?: (event: { value: Value }) => void;
  className?: string;
  attachments?: Attachment[];
  onRemoveAttachment?: (index: number) => void;
  onDeleteDraft?: () => void;
  isSendingOrDeleting?: boolean;
  actions?: ReactNode;
}) {
  const { editor } = editorState;
  const hasAttachments = attachments !== undefined && attachments.length > 0;

  return (
    <div
      className={cn(
        "flex flex-col rounded-md border border-input bg-background pb-2 overflow-hidden",
        "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
        className,
      )}
    >
      {header}
      <Plate editor={editor} onChange={onChange}>
        {toolbar}
        <PlateContent
          className={cn(
            "max-h-[200px] overflow-y-auto outline-none p-3",
            "text-sm placeholder:text-muted-foreground",
            // Reset placeholder styles — Slate inherits bold/italic/underline from the current marks
            "[&_[data-slate-placeholder]]:!font-normal [&_[data-slate-placeholder]]:!not-italic [&_[data-slate-placeholder]]:!no-underline [&_[data-slate-placeholder]]:![text-decoration:none]",
          )}
          placeholder={placeholder}
        />
      </Plate>
      {hasAttachments && (
        <div className="flex flex-wrap gap-2 border-t border-border px-3 py-2">
          {attachments.map((attachment, index) => {
            const description = Option.getOrElse(
              attachment.description,
              () => "Attachment",
            );
            const displayName =
              description || attachment.url.split("/").pop() || "Attachment";

            return (
              <div
                className="flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-xs"
                key={attachment.id}
              >
                <PaperclipIcon className="size-3 shrink-0 text-muted-foreground" />
                <span className="max-w-[150px] truncate">{displayName}</span>
                <button
                  className="ml-0.5 rounded-sm p-0.5 hover:bg-background"
                  onClick={() => onRemoveAttachment?.(index)}
                  type="button"
                >
                  <XCloseIcon className="size-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {actions && (
        <Flex
          align="center"
          className="min-w-0 overflow-hidden"
          gap="xs"
          justify="flex-end"
          pt="md"
          px="lg"
        >
          {actions}
        </Flex>
      )}
    </div>
  );
});
